import React, { useEffect, useState } from 'react';
import { api, BACKEND_URL } from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Plus, Edit, Trash2, Upload, Loader2, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const Products = () => {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [importing, setImporting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    price: '',
    cost_price: '',
    stock: '',
    min_stock: '10',
    category: 'bebida'
  });

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await api.products.getAll();
      setProducts(response.data);
    } catch (error) {
      toast.error('Erro ao carregar produtos');
    } finally {
      setLoading(false);
    }
  };

  const handleNotifyRestock = () => {
    const lowStockProducts = products.filter(p => p.stock <= p.min_stock);

    if (lowStockProducts.length === 0) {
      toast.success('Estoque em dia! Nenhum produto precisando de reposição no momento.');
      return;
    }

    let message = `⚠️ *Alerta de Estoque Baixo - Bar Cabrones* ⚠️\n\nPrecisamos repor os seguintes itens:\n\n`;
    
    lowStockProducts.forEach(p => {
      message += `- *${p.name}* (Temos ${p.stock} | Mínimo é ${p.min_stock})\n`;
    });
    
    message += `\nPor favor, providenciar a compra.`;

    const url = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        price: parseFloat(formData.price),
        cost_price: parseFloat(formData.cost_price || 0),
        stock: parseInt(formData.stock),
        min_stock: parseInt(formData.min_stock)
      };
      
      if (editingProduct) {
        await api.products.update(editingProduct.id, data);
        toast.success('Produto atualizado!');
      } else {
        await api.products.create(data);
        toast.success('Produto criado!');
      }
      
      setDialogOpen(false);
      resetForm();
      fetchProducts();
    } catch (error) {
      toast.error('Erro ao salvar produto');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este produto?')) return;
    try {
      await api.products.delete(id);
      toast.success('Produto excluído!');
      fetchProducts();
    } catch (error) {
      toast.error('Erro ao excluir produto');
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImporting(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/import/products`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      if (response.ok) {
        toast.success('Produtos importados com sucesso!');
        setImportDialogOpen(false);
        fetchProducts();
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Erro na importação');
      }
    } catch (error) {
      console.error(error);
      toast.error('Erro ao importar produtos');
    } finally {
      setImporting(false);
      e.target.value = ''; // Reset input
    }
  };

  const openEditDialog = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      price: product.price.toString(),
      cost_price: (product.cost_price || 0).toString(),
      stock: product.stock.toString(),
      min_stock: product.min_stock.toString(),
      category: product.category
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingProduct(null);
    setFormData({
      name: '',
      price: '',
      cost_price: '',
      stock: '',
      min_stock: '10',
      category: 'bebida'
    });
  };

  return (
    <div className="space-y-6" data-testid="products-page">
      <div className="flex items-center justify-between">
        <h1 className="text-5xl font-bold uppercase tracking-tight">Produtos</h1>
        <div className="flex gap-2">
          
          <Button 
            variant="outline" 
            className="border-green-600 text-green-600 hover:bg-green-600 hover:text-white uppercase font-bold transition-colors"
            onClick={handleNotifyRestock}
          >
            <MessageCircle className="mr-2 h-5 w-5" /> Avisar Diretoria
          </Button>

          {user?.role === 'admin' && (
            <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="border-primary text-primary hover:bg-primary/10 uppercase font-bold">
                  <Upload className="mr-2 h-5 w-5" /> Importar
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-card border-border">
                <DialogHeader>
                  <DialogTitle className="text-2xl uppercase font-bold">Importar Produtos</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Selecione um arquivo CSV ou Excel (.xlsx) com as colunas: name, price, stock, min_stock, category
                  </p>
                  <div className="flex items-center justify-center w-full">
                    <label htmlFor="dropzone-file-products" className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer hover:bg-secondary/50 border-border">
                      <div className="flex flex-col items-center justify-center pt-5 pb-6">
                        {importing ? (
                          <Loader2 className="w-8 h-8 mb-4 text-primary animate-spin" />
                        ) : (
                          <Upload className="w-8 h-8 mb-4 text-muted-foreground" />
                        )}
                        <p className="mb-2 text-sm text-muted-foreground"><span className="font-semibold">Clique para enviar</span></p>
                      </div>
                      <input id="dropzone-file-products" type="file" className="hidden" accept=".csv, .xlsx" onChange={handleImport} disabled={importing} />
                    </label>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          )}

          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold uppercase" data-testid="add-product-button">
                <Plus className="mr-2 h-5 w-5" /> Novo Produto
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border" data-testid="product-dialog">
              <DialogHeader>
                <DialogTitle className="text-2xl uppercase font-bold">
                  {editingProduct ? 'Editar Produto' : 'Novo Produto'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider">Nome</Label>
                  <Input
                    data-testid="product-name-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="mt-1 font-mono"
                    required
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider">Categoria</Label>
                  <Input
                    data-testid="product-category-input"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="mt-1 font-mono"
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs uppercase tracking-wider">Preço de Custo (R$)</Label>
                    <Input
                      data-testid="product-cost-input"
                      type="number"
                      step="0.01"
                      value={formData.cost_price}
                      onChange={(e) => setFormData({ ...formData, cost_price: e.target.value })}
                      className="mt-1 font-mono"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider">Preço de Venda (R$)</Label>
                    <Input
                      data-testid="product-price-input"
                      type="number"
                      step="0.01"
                      value={formData.price}
                      onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                      className="mt-1 font-mono"
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs uppercase tracking-wider">Estoque Atual</Label>
                    <Input
                      data-testid="product-stock-input"
                      type="number"
                      value={formData.stock}
                      onChange={(e) => setFormData({ ...formData, stock: e.target.value })}
                      className="mt-1 font-mono"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider">Estoque Mínimo</Label>
                    <Input
                      data-testid="product-min-stock-input"
                      type="number"
                      value={formData.min_stock}
                      onChange={(e) => setFormData({ ...formData, min_stock: e.target.value })}
                      className="mt-1 font-mono"
                      required
                    />
                  </div>
                </div>
                <Button type="submit" className="w-full bg-primary hover:bg-primary/90 font-bold uppercase h-12" data-testid="product-submit-button">
                  {editingProduct ? 'Atualizar' : 'Criar'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card className="bg-card border-border" data-testid="products-list">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-muted-foreground font-mono uppercase text-xs">Nome</TableHead>
                <TableHead className="text-muted-foreground font-mono uppercase text-xs">Categoria</TableHead>
                <TableHead className="text-muted-foreground font-mono uppercase text-xs">Custo</TableHead>
                <TableHead className="text-muted-foreground font-mono uppercase text-xs">Venda</TableHead>
                <TableHead className="text-muted-foreground font-mono uppercase text-xs">Estoque</TableHead>
                <TableHead className="text-muted-foreground font-mono uppercase text-xs">Mínimo</TableHead>
                <TableHead className="text-muted-foreground font-mono uppercase text-xs text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {products.map((product) => (
                <TableRow key={product.id} className="border-border/50 hover:bg-secondary/50" data-testid={`product-row-${product.id}`}>
                  <TableCell className="font-medium">{product.name}</TableCell>
                  <TableCell className="text-muted-foreground">{product.category}</TableCell>
                  <TableCell className="font-mono text-muted-foreground">R$ {(product.cost_price || 0).toFixed(2)}</TableCell>
                  <TableCell className="font-mono font-bold">R$ {product.price.toFixed(2)}</TableCell>
                  <TableCell className={`font-mono font-bold ${product.stock <= product.min_stock ? 'text-destructive' : 'text-foreground'}`}>
                    {product.stock}
                  </TableCell>
                  <TableCell className="font-mono text-muted-foreground">{product.min_stock}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openEditDialog(product)}
                        className="hover:bg-primary/10 hover:text-primary"
                        data-testid={`edit-product-${product.id}`}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(product.id)}
                        className="hover:bg-destructive/10 hover:text-destructive"
                        data-testid={`delete-product-${product.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default Products;
